import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Collapse, Form, Row, Spinner } from 'react-bootstrap';
import { ChevronDown, ChevronRight } from 'react-bootstrap-icons';
import { ConfigField } from '@/components/ConfigField';
import { FeatureChecklist } from '@/components/FeatureChecklist';
import {
  ALL_APPS,
  CONFIG_SECTIONS,
  FIELDS,
  PROD_APPS,
  createModeInitialValues,
  effectiveCurrentValue,
  hydrateFieldValue,
  hydrateFormValues,
  isNonDefault,
  isOnSwitch,
  serializeFieldValue,
  type FieldDef,
  type FormValues,
  type SectionId,
} from '@/config/clientConfigFields';
import {
  platformSettingsService,
  PLATFORM_QUOTA_INITIAL_VALUES,
  PlatformSettings as PlatformSettingsType,
} from '@/services/platformSettingsService';
import { type ClientConfig } from '@/types';
import {
  DEFAULT_ADMIN_FEATURES,
  DEFAULT_STANDARD_FEATURES,
  featuresListToString,
  sameMembers,
  stringToFeatures,
} from '@/constants/features';

export type AppDeploymentMode = 'all' | 'prod' | 'custom';

export interface ClientConfigFormProps {
  mode: 'update' | 'create';
  /** Update mode: the selected client's stored config record. */
  currentConfig?: ClientConfig;
  working: boolean;
  submitLabel: string;
  submitDisabled?: boolean;
  submitHint?: string;
  /** Receives the built payload (update: per-key diff, create: minimal config). */
  onSubmit: (payload: Partial<ClientConfig>) => void;
  onError: (message: string) => void;
  /** Notifies the page when the Region field changes (e.g. for the Quota Check tab). */
  onRegionChange?: (region: string) => void;
  /**
   * Notifies the page when the form gains/loses unsaved user edits — used to
   * gate actions that would re-hydrate the form (e.g. hygiene-panel removal).
   */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Increment after each successful save — shows a green "Saved" note in the
   * rail until the user edits another setting.
   */
  saveCounter?: number;
}

/** Human note for what removal of a key falls back to. */
const removeFallbackNote = (def: FieldDef): string => {
  if (def.inheritKey) return 'Falls back to the inherited platform setting.';
  const d = String(def.default);
  return `Falls back to default: ${d === '' ? '(not set)' : d}.`;
};

const searchHaystack = (def: FieldDef): string => `${def.key} ${def.label} ${def.help ?? ''}`.toLowerCase();

/**
 * Empty/missing group lists display as the recommended defaults — the diff
 * must use the SAME substitution as hydration, or a record holding an empty
 * list would silently rewrite to defaults on every unrelated save.
 */
const effectiveGroupList = (list: string[] | undefined, defaults: string[]): string[] =>
  list && list.length > 0 ? list : defaults;

// Search keywords for the two bespoke (non-registry) controls
const APPS_CONTROL_HAYSTACK = `apps app deployment all production custom selection ${ALL_APPS.join(' ')}`.toLowerCase();
const GROUPS_CONTROL_HAYSTACK = 'groups features standard admin user feature checklist permissions'.toLowerCase();

/** Sub-group heading inside a section: bold title + a plain-language description. */
function SubHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="mt-3 mb-2 border-bottom pb-1">
      <div className="fw-semibold">{title}</div>
      <div className="text-muted small">{description}</div>
    </div>
  );
}

export function ClientConfigForm({
  mode,
  currentConfig,
  working,
  submitLabel,
  submitDisabled = false,
  submitHint,
  onSubmit,
  onError,
  onRegionChange,
  onDirtyChange,
  saveCounter = 0,
}: ClientConfigFormProps) {
  // ---- registry-driven field state -----------------------------------------
  const [values, setValues] = useState<FormValues>(() =>
    mode === 'update' ? hydrateFormValues(currentConfig) : createModeInitialValues()
  );
  // Keys marked for removal from the record (Update mode only) — saved as
  // REMOVE expressions via updateClientConfig's undefined-value convention.
  const [removedKeys, setRemovedKeys] = useState<Set<string>>(new Set());

  // ---- bespoke controls -----------------------------------------------------
  // New clients start on "Production apps" (long-standing CS default).
  const [appMode, setAppMode] = useState<AppDeploymentMode>(mode === 'create' ? 'prod' : 'custom');
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [groupAdmin, setGroupAdmin] = useState(featuresListToString(DEFAULT_ADMIN_FEATURES));
  const [groupStandard, setGroupStandard] = useState(featuresListToString(DEFAULT_STANDARD_FEATURES));

  // ---- UI state -------------------------------------------------------------
  // True once the user has edited anything since the last hydration. Reported
  // upward so the page can block form-resetting actions while edits are unsaved.
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty]);
  // Green "Saved" note shown after a successful save, until the next edit.
  const [justSaved, setJustSaved] = useState(false);
  const markEdited = () => {
    setDirty(true);
    setJustSaved(false);
  };
  const [search, setSearch] = useState('');
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(CONFIG_SECTIONS.map((s) => [s.id, mode === 'create']))
  );

  // Live platform-settings (Level 1) so the quota inheritance placeholders
  // show what clients actually inherit, not a hardcoded guess.
  const [platformSettings, setPlatformSettings] = useState<PlatformSettingsType | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await platformSettingsService.get();
        if (!cancelled) setPlatformSettings(s);
      } catch {
        if (!cancelled) setPlatformSettings({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const inherited = <K extends keyof typeof PLATFORM_QUOTA_INITIAL_VALUES>(
    key: K
  ): (typeof PLATFORM_QUOTA_INITIAL_VALUES)[K] => {
    const fromRecord = platformSettings?.[key as keyof PlatformSettingsType];
    if (typeof fromRecord === typeof PLATFORM_QUOTA_INITIAL_VALUES[key] && fromRecord !== undefined) {
      return fromRecord as (typeof PLATFORM_QUOTA_INITIAL_VALUES)[K];
    }
    return PLATFORM_QUOTA_INITIAL_VALUES[key];
  };

  const inheritPlaceholder = (key: keyof typeof PLATFORM_QUOTA_INITIAL_VALUES, suffix = ''): string => {
    if (platformSettings === null) return 'Leave empty to inherit (loading…)';
    return `Leave empty to inherit (Platform Settings → ${inherited(key).toLocaleString()}${suffix})`;
  };

  // ---- hydration (Update mode re-hydrates whenever the selected client changes)
  useEffect(() => {
    if (mode !== 'update') return;
    const cfg = currentConfig;
    setValues(hydrateFormValues(cfg));
    setRemovedKeys(new Set());
    const allApps = Boolean((cfg as Record<string, unknown> | undefined)?.allApps);
    const allProd = Boolean(cfg?.allProdApps);
    setAppMode(allApps ? 'all' : allProd ? 'prod' : 'custom');
    // Hydrate the apps map regardless of mode so dev-app additions under
    // "Production apps" survive an unrelated save (the old page dropped them).
    setSelectedApps(cfg?.apps ? Object.keys(cfg.apps) : []);
    const groups = (cfg as Record<string, unknown> | undefined)?.groups as
      | { admin?: string[]; standard?: string[] }
      | undefined;
    setGroupAdmin(featuresListToString(effectiveGroupList(groups?.admin, DEFAULT_ADMIN_FEATURES)));
    setGroupStandard(featuresListToString(effectiveGroupList(groups?.standard, DEFAULT_STANDARD_FEATURES)));
    setDirty(false);
    // Cleared on client switch; the saveCounter effect below re-sets it right
    // after a save (it runs later in the same commit).
    setJustSaved(false);
    // Auto-open the sections where this client differs from defaults.
    const next: Record<string, boolean> = {};
    for (const s of CONFIG_SECTIONS) {
      next[s.id] = FIELDS.some((f) => f.section === s.id && isNonDefault(f, hydrateFieldValue(f, cfg)));
    }
    next.core = true;
    next.apps = next.apps || allApps || allProd || Boolean(cfg?.apps && Object.keys(cfg.apps).length);
    setOpenSections(next);
  }, [mode, currentConfig]);

  // Runs after the hydration effect in the same commit, so a save (which both
  // re-hydrates and bumps saveCounter) lands on "Saved" rather than reset.
  // Compared against the mount-time value via a ref — a remount with a stale
  // non-zero counter (clear selection, then pick another client) must NOT
  // show "Saved" for a client that was never saved.
  const lastSaveCounter = useRef(saveCounter);
  useEffect(() => {
    if (saveCounter > lastSaveCounter.current) setJustSaved(true);
    lastSaveCounter.current = saveCounter;
  }, [saveCounter]);

  const setValue = (key: string, v: FormValues[string]) => {
    if (key === 'region' && typeof v === 'string') onRegionChange?.(v);
    markEdited();
    setValues((prev) => {
      const next = { ...prev, [key]: v };
      // Keep dynamic-option selects valid: if this change leaves a field's current
      // choice unavailable (dropped OR greyed-out/disabled), snap it to the first
      // ENABLED option (e.g. Default Model when Bedrock-only removes Standard, or
      // a non-dev instance greys out Expert).
      for (const def of FIELDS) {
        if (!def.dynamicOptions) continue;
        const enabled = def.dynamicOptions(next).filter((o) => !o.disabled);
        if (enabled.length > 0 && !enabled.some((o) => o.value === next[def.key])) {
          next[def.key] = enabled[0].value;
        }
      }
      // Auto-off switches that become disabled: a `disabledForcesOff` toggle must
      // not sit visually "On" while greyed out (e.g. the model picker once the set
      // collapses to one model). Flip it off so stored + displayed state agree.
      for (const def of FIELDS) {
        if (def.type !== 'switch' || !def.disabledForcesOff) continue;
        if (def.disabledWhen?.(next) && next[def.key] === true) {
          next[def.key] = false;
        }
      }
      return next;
    });
    setRemovedKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const selectAppMode = (m: AppDeploymentMode) => {
    markEdited();
    setAppMode(m);
  };
  const editGroupAdmin = (v: string) => {
    markEdited();
    setGroupAdmin(v);
  };
  const editGroupStandard = (v: string) => {
    markEdited();
    setGroupStandard(v);
  };

  // ---- modified/non-default helpers -----------------------------------------
  const groupsNonDefault =
    !sameMembers(stringToFeatures(groupAdmin), DEFAULT_ADMIN_FEATURES) ||
    !sameMembers(stringToFeatures(groupStandard), DEFAULT_STANDARD_FEATURES);

  /**
   * "How many settings are ON" — the section-bubble / rail count. Switches
   * count when true; text/number/tri-state count when a value is set;
   * optional selects (empty-able default, e.g. AgentCore Region) count when
   * chosen. Always-set selects (Region, Numa Files backend) never count.
   */
  const fieldIsOn = (def: FieldDef): boolean => {
    if (removedKeys.has(def.key)) return false;
    if (def.visibleWhen && !def.visibleWhen(values)) return false;
    const v = values[def.key];
    switch (def.type) {
      case 'switch':
        return v === true;
      case 'tri-state':
        return v !== '';
      case 'number':
      case 'text':
        return String(v).trim() !== '';
      case 'select':
        return def.default === '' && v !== '';
    }
  };

  const onCount = (sectionId: SectionId): number =>
    FIELDS.filter((f) => f.section === sectionId && fieldIsOn(f)).length;

  // ---- payload builders ------------------------------------------------------
  /**
   * @param materializeOnDefaults When true (the actual Save), force-write every
   *   ON switch that is absent from the record so on-by-default flags land in
   *   config.json. When false (the live "N pending changes" indicator), skip
   *   that force-write so a pristine open of a legacy record — one that simply
   *   never stored its default:true switches — reads "No pending changes"
   *   instead of nagging about flags the operator never touched. The
   *   normalization is still applied (and shown in the confirm-diff preview) at
   *   Save time; it is just not counted as an unsaved user edit.
   */
  const buildUpdatePayload = (materializeOnDefaults: boolean): Partial<ClientConfig> => {
    const cfg = (currentConfig ?? {}) as Record<string, unknown>;
    const updates: Record<string, unknown> = {};

    for (const def of FIELDS) {
      if (removedKeys.has(def.key)) {
        if (cfg[def.key] !== undefined) updates[def.key] = undefined;
        continue;
      }
      const newVal = serializeFieldValue(def, values[def.key]);
      // Ensure alwaysWrite fields are written even when default and currently missing
      if (def.alwaysWrite && cfg[def.key] === undefined) {
        updates[def.key] = newVal;
        continue;
      }
      // Force-write ON switches that are absent from the record, so on-by-default
      // flags (default: true) materialize as an explicit `true` instead of hiding
      // behind the deploy-time default. Gated-off switches are skipped — a hidden
      // sub-flag (e.g. a Connect option with Voice off) must never be conjured in.
      if (
        materializeOnDefaults &&
        isOnSwitch(def, values[def.key]) &&
        cfg[def.key] === undefined &&
        (!def.visibleWhen || def.visibleWhen(values))
      ) {
        updates[def.key] = true;
        continue;
      }
      const eff = effectiveCurrentValue(def, cfg as Partial<ClientConfig>);
      if (eff === newVal) continue;
      // Both "absent" — nothing to remove
      if (newVal === undefined && cfg[def.key] === undefined) continue;
      updates[def.key] = newVal;
    }

    // App deployment (allApps / allProdApps / apps map)
    const allAppsNew = appMode === 'all';
    const allProdNew = appMode === 'prod';
    const effAllApps = Boolean(cfg.allApps);
    const effAllProd = Boolean(cfg.allProdApps);
    if (effAllApps !== allAppsNew) updates.allApps = allAppsNew;
    if (!allAppsNew && effAllProd !== allProdNew) updates.allProdApps = allProdNew;
    if (!allAppsNew) {
      const appsToInclude = allProdNew ? selectedApps.filter((a) => !PROD_APPS.includes(a)) : selectedApps;
      const currentApps = Object.keys((cfg.apps as Record<string, unknown> | undefined) || {});
      if (!sameMembers(currentApps, appsToInclude)) {
        updates.apps = appsToInclude.length > 0 ? Object.fromEntries(appsToInclude.map((a) => [a, {}])) : {};
      }
    }

    // Legacy field cleanup: `triggers` was renamed to `eventTriggers`. If a
    // record still carries the old key, drop it on save (REMOVE expression —
    // `undefined` value is the signal). Idempotent on subsequent saves.
    if (cfg.triggers !== undefined) updates.triggers = undefined;

    // Groups: include only when membership actually differs from the record's
    // effective groups (defaults when absent).
    const admin = stringToFeatures(groupAdmin);
    const standard = stringToFeatures(groupStandard);
    const currentGroups = cfg.groups as { admin?: string[]; standard?: string[] } | undefined;
    const currentAdmin = effectiveGroupList(currentGroups?.admin, DEFAULT_ADMIN_FEATURES);
    const currentStandard = effectiveGroupList(currentGroups?.standard, DEFAULT_STANDARD_FEATURES);
    if (!sameMembers(admin, currentAdmin) || !sameMembers(standard, currentStandard)) {
      updates.groups = {
        ...(standard.length ? { standard } : {}),
        ...(admin.length ? { admin } : {}),
      };
    }

    return updates as Partial<ClientConfig>;
  };

  const buildCreatePayload = (): Partial<ClientConfig> => {
    const minimal: Record<string, unknown> = {};

    for (const def of FIELDS) {
      // Fields hidden by their gate (e.g. Connect fields with Voice off) are
      // not written — their state is not part of the configured surface.
      if (def.visibleWhen && !def.visibleWhen(values)) continue;
      const newVal = serializeFieldValue(def, values[def.key]);
      if (def.key === 'region' || def.alwaysWrite) {
        minimal[def.key] = newVal;
        continue;
      }
      // Force-write ON switches even when they equal the schema default, so the
      // created config.json always lists enabled features (on-by-default flags
      // included). Gated-off fields were already skipped above.
      if (isOnSwitch(def, values[def.key])) {
        minimal[def.key] = true;
        continue;
      }
      if (newVal === undefined) continue;
      const defaultSerialized = serializeFieldValue(def, hydrateFieldValue(def, undefined));
      if (newVal !== defaultSerialized) minimal[def.key] = newVal;
    }

    // AgentCore region equal to the deployment region is redundant — drop it.
    if (minimal.agentCoreRegion !== undefined && minimal.agentCoreRegion === minimal.region) {
      delete minimal.agentCoreRegion;
    }

    // App deployment
    const allAppsNew = appMode === 'all';
    const allProdNew = appMode === 'prod';
    if (allAppsNew) minimal.allApps = true;
    else if (allProdNew) minimal.allProdApps = true;
    if (!allAppsNew) {
      const appsToInclude = allProdNew ? selectedApps.filter((a) => !PROD_APPS.includes(a)) : selectedApps;
      if (appsToInclude.length > 0) minimal.apps = Object.fromEntries(appsToInclude.map((a) => [a, {}]));
    }

    // Groups only when the membership differs from the recommended defaults
    const admin = stringToFeatures(groupAdmin);
    const standard = stringToFeatures(groupStandard);
    if (groupsNonDefault && (admin.length || standard.length)) {
      minimal.groups = {
        ...(standard.length ? { standard } : {}),
        ...(admin.length ? { admin } : {}),
      };
    }

    return minimal as Partial<ClientConfig>;
  };

  // Live indicator: count only genuine user edits — pass false so absent
  // on-by-default flags don't register as phantom pending changes on open.
  const pendingCount = useMemo(
    () => Object.keys(mode === 'update' ? buildUpdatePayload(false) : buildCreatePayload()).length,
    [values, removedKeys, appMode, selectedApps, groupAdmin, groupStandard, currentConfig, mode]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = FIELDS.map((def) => {
      if (!def.validate) return null;
      // A key pending removal is never serialized — its stale stored value
      // must not block the save that removes it.
      if (removedKeys.has(def.key)) return null;
      const raw = String(values[def.key] ?? '');
      return raw ? def.validate(raw) : null;
    }).filter(Boolean) as string[];
    if (errors.length) {
      onError(errors.join('; '));
      return;
    }
    // Actual save: materialize on-by-default flags into the written record.
    onSubmit(mode === 'update' ? buildUpdatePayload(true) : buildCreatePayload());
  };

  // ---- filtering -------------------------------------------------------------
  const searchLower = search.trim().toLowerCase();
  const filtersActive = searchLower.length > 0;

  const fieldVisible = (def: FieldDef): boolean => {
    if (def.visibleWhen && !def.visibleWhen(values)) return false;
    if (searchLower && !searchHaystack(def).includes(searchLower)) return false;
    return true;
  };

  const appsControlVisible = !searchLower || APPS_CONTROL_HAYSTACK.includes(searchLower);
  const groupsControlVisible = !searchLower || GROUPS_CONTROL_HAYSTACK.includes(searchLower);

  const sectionHasContent = (sectionId: SectionId): boolean => {
    if (FIELDS.some((f) => f.section === sectionId && fieldVisible(f))) return true;
    if (sectionId === 'apps' && appsControlVisible) return true;
    if (sectionId === 'security' && groupsControlVisible) return true;
    return false;
  };

  const scrollToSection = (sectionId: SectionId) => {
    setOpenSections((prev) => ({ ...prev, [sectionId]: true }));
    // Defer until the Collapse has rendered its content
    setTimeout(() => {
      document.getElementById(`cfg-section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  // ---- rendering ---------------------------------------------------------------
  const renderField = (def: FieldDef) => {
    const record = (currentConfig ?? {}) as Record<string, unknown>;
    const fieldDisabled = def.disabledWhen ? def.disabledWhen(values) : false;
    const removable =
      mode === 'update' &&
      record[def.key] !== undefined &&
      def.key !== 'region' &&
      !def.alwaysWrite &&
      !removedKeys.has(def.key);
    return (
      <ConfigField
        key={def.key}
        label={def.label}
        value={values[def.key]}
        defaultValue={hydrateFieldValue(def, undefined)}
        onChange={(v: string | boolean) => setValue(def.key, v)}
        type={def.type}
        options={def.dynamicOptions ? def.dynamicOptions(values) : def.options}
        placeholder={def.inheritKey ? inheritPlaceholder(def.inheritKey, def.inheritSuffix) : def.placeholder}
        helpText={def.help}
        warnWhenSet={def.warnWhenSet}
        disabled={fieldDisabled}
        disabledNote={fieldDisabled ? def.disabledNote : undefined}
        removable={removable}
        removed={removedKeys.has(def.key)}
        onRemove={() => {
          markEdited();
          setRemovedKeys((prev) => new Set(prev).add(def.key));
        }}
        onUndoRemove={() => {
          markEdited();
          setRemovedKeys((prev) => {
            const next = new Set(prev);
            next.delete(def.key);
            return next;
          });
        }}
        removeFallbackNote={removeFallbackNote(def)}
      />
    );
  };

  /**
   * Top-level fields of a section: no childOf parent, and either visible
   * themselves or carrying at least one visible child (so a search hit on a
   * child still surfaces its group).
   */
  const topLevelFields = (sectionId: SectionId): FieldDef[] =>
    FIELDS.filter(
      (f) =>
        f.section === sectionId &&
        !f.childOf &&
        (fieldVisible(f) || FIELDS.some((c) => c.childOf === f.key && fieldVisible(c)))
    );

  /**
   * Render a field plus its conditional children in the same indented
   * sub-panel styling as the Automations sub-options.
   */
  const renderFieldWithChildren = (def: FieldDef) => {
    const children = FIELDS.filter((f) => f.childOf === def.key && fieldVisible(f));
    return (
      <div key={def.key}>
        {fieldVisible(def) && renderField(def)}
        {children.length > 0 && (
          <div className="border-start border-3 ps-3 pe-3 ms-2 mb-3 bg-light bg-opacity-50 rounded-end py-3">
            {children.map(renderField)}
          </div>
        )}
      </div>
    );
  };

  const renderAppsControl = () => (
    <Form.Group className="mb-3">
      <Form.Label className="fw-semibold">App deployment</Form.Label>
      <Form.Check
        type="radio"
        id="app-mode-all"
        name="app-mode"
        label="All apps — every app in the library (recommended for dev/demo stacks)"
        checked={appMode === 'all'}
        onChange={() => selectAppMode('all')}
      />
      <Form.Check
        type="radio"
        id="app-mode-prod"
        name="app-mode"
        label="Production apps — every production app; dev apps can be added below"
        checked={appMode === 'prod'}
        onChange={() => selectAppMode('prod')}
      />
      <Form.Check
        type="radio"
        id="app-mode-custom"
        name="app-mode"
        label="Custom selection — pick apps individually"
        checked={appMode === 'custom'}
        onChange={() => selectAppMode('custom')}
      />
      <div className="mt-3">
        <Form.Label className="small">{appMode === 'custom' ? 'Select Specific Apps' : 'Included Apps'}</Form.Label>
        <div className="d-flex flex-column gap-1">
          {ALL_APPS.map((a) => {
            const isProdApp = PROD_APPS.includes(a);
            const isChecked = appMode === 'all' || (appMode === 'prod' && isProdApp) || selectedApps.includes(a);
            const isDisabled = appMode === 'all' || (appMode === 'prod' && isProdApp);
            return (
              <Form.Check
                key={a}
                type="checkbox"
                id={`app-${a}`}
                label={a}
                checked={isChecked}
                disabled={isDisabled}
                onChange={(e) => {
                  const checked = e.currentTarget.checked;
                  markEdited();
                  setSelectedApps((prev) =>
                    checked ? Array.from(new Set([...prev, a])) : prev.filter((x) => x !== a)
                  );
                }}
              />
            );
          })}
        </div>
        <Form.Text className="text-muted">
          {appMode === 'all'
            ? 'All applications are included'
            : appMode === 'prod'
              ? 'All production apps are included (dev apps can be selected individually)'
              : 'Tick one or more applications'}
        </Form.Text>
      </div>
    </Form.Group>
  );

  const renderGroupsControl = () => (
    <div className="border-top pt-3 mt-2">
      <div className="text-muted small fw-semibold mb-2 text-uppercase">User feature groups</div>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Standard User Features</Form.Label>
            <FeatureChecklist value={groupStandard} onChange={editGroupStandard} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Admin User Features</Form.Label>
            <FeatureChecklist value={groupAdmin} onChange={editGroupAdmin} />
          </Form.Group>
        </Col>
      </Row>
      <div className="text-muted small">
        Leave unchanged to use system defaults. Modify only if you need to override the recommended groups.
      </div>
    </div>
  );

  const renderSection = (sectionId: SectionId) => {
    const section = CONFIG_SECTIONS.find((s) => s.id === sectionId)!;
    if (!sectionHasContent(sectionId)) return null;
    const open = filtersActive || openSections[sectionId];
    const onBadge = onCount(sectionId);
    const sectionFields = FIELDS.filter((f) => f.section === sectionId && fieldVisible(f));
    return (
      <Card
        key={sectionId}
        className="border-0 shadow-sm mb-3"
        id={`cfg-section-${sectionId}`}
        style={{ scrollMarginTop: '1rem' }}
      >
        <Card.Header
          role="button"
          onClick={() => setOpenSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }))}
          className="d-flex align-items-center bg-white"
        >
          {open ? <ChevronDown className="me-2" /> : <ChevronRight className="me-2" />}
          <span className="fw-semibold">{section.title}</span>
          {section.description && (
            <span className="text-muted small ms-2 d-none d-lg-inline">{section.description}</span>
          )}
          {onBadge > 0 && (
            <Badge bg="primary" className="ms-auto">
              {onBadge} on
            </Badge>
          )}
        </Card.Header>
        <Collapse in={open}>
          <div>
            <Card.Body>
              {sectionId === 'apps' && appsControlVisible && renderAppsControl()}
              {sectionId === 'automations'
                ? renderAutomationsFields(sectionFields)
                : topLevelFields(sectionId).map(renderFieldWithChildren)}
              {sectionId === 'security' && groupsControlVisible && renderGroupsControl()}
            </Card.Body>
          </div>
        </Collapse>
      </Card>
    );
  };

  /**
   * Automations keeps its bespoke layout: master switch, then an indented
   * panel with triggers + quota subgroups under uppercase mini-headings.
   */
  const renderAutomationsFields = (visibleFields: FieldDef[]) => {
    const visible = new Set(visibleFields.map((f) => f.key));
    const get = (key: string) => {
      const def = FIELDS.find((f) => f.key === key);
      return def && visible.has(key) ? renderField(def) : null;
    };
    const schedulingOn = values.scheduling === true;
    const triggersOn = schedulingOn && values.eventTriggers === true;
    // Everything under the master switch splits into two columns — Schedules
    // (always available when automations are on) and Event Triggers (opt-in
    // with its own toggle + quotas) — plus the combined concurrency caps.
    const scheduleKeys = [
      'schedulingMinIntervalMinutes',
      'maxRunsPerCompanyPerMonth',
      'maxRunsPerUserPerMonth',
      'requireApprovalAboveUserCap',
    ];
    const triggerKeys = ['eventTriggers', 'maxTriggerRunsPerCompanyPerMonth', 'maxTriggerRunsPerUserPerMonth'];
    const concurrencyKeys = ['maxConcurrentActiveSchedulesPerCompany', 'maxConcurrentActiveSchedulesPerUser'];
    const subFieldsVisible = [...scheduleKeys, ...triggerKeys, ...concurrencyKeys].some((k) => visible.has(k));
    return (
      <>
        {get('scheduling')}
        {schedulingOn && subFieldsVisible && (
          <div className="border-start border-3 ps-3 pe-3 ms-2 mb-3 bg-light bg-opacity-50 rounded-end py-3">
            {scheduleKeys.some((k) => visible.has(k)) && (
              <>
                <SubHeader
                  title="Schedules"
                  description="Recurring automations that run on a cron schedule. Quotas are projected from each schedule's frequency."
                />
                {get('schedulingMinIntervalMinutes')}
                {get('maxRunsPerCompanyPerMonth')}
                {get('maxRunsPerUserPerMonth')}
                {get('requireApprovalAboveUserCap')}
              </>
            )}
            {triggerKeys.some((k) => visible.has(k)) && (
              <>
                <SubHeader
                  title="Event Triggers"
                  description="Automations fired by external events (e.g. a Slack message or Gmail email). Quotas count actual fires."
                />
                {get('eventTriggers')}
                {get('maxTriggerRunsPerCompanyPerMonth')}
                {get('maxTriggerRunsPerUserPerMonth')}
              </>
            )}
            {concurrencyKeys.some((k) => visible.has(k)) && (
              <>
                <SubHeader
                  title="Concurrency Limits"
                  description={
                    triggersOn
                      ? 'How many automations (schedules + event triggers combined) can be active at the same time.'
                      : 'How many schedules can be active at the same time.'
                  }
                />
                {get('maxConcurrentActiveSchedulesPerCompany')}
                {get('maxConcurrentActiveSchedulesPerUser')}
              </>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        {/* Sticky rail: search, filters, section nav, save */}
        <Col md={4} lg={3} xl={2} className="mb-3">
          <div className="position-sticky" style={{ top: '1rem' }}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-3">
                <Form.Control
                  size="sm"
                  className="mb-3"
                  placeholder="Search settings…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    // Never let Enter in the search box implicitly submit the form
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                  aria-label="Search settings"
                />
                <div className="d-flex flex-column gap-1 mb-3">
                  {CONFIG_SECTIONS.map((s) => {
                    const onBadge = onCount(s.id);
                    if (!sectionHasContent(s.id)) return null;
                    return (
                      <Button
                        key={s.id}
                        variant="link"
                        size="sm"
                        className="p-0 text-start text-decoration-none d-flex align-items-center"
                        onClick={() => scrollToSection(s.id)}
                      >
                        <span className="text-truncate">{s.title}</span>
                        {onBadge > 0 && (
                          <Badge bg="primary" pill className="ms-auto">
                            {onBadge}
                          </Badge>
                        )}
                      </Button>
                    );
                  })}
                </div>
                <div className="d-grid">
                  <Button type="submit" disabled={working || submitDisabled}>
                    {working ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving…
                      </>
                    ) : (
                      submitLabel
                    )}
                  </Button>
                </div>
                {justSaved && pendingCount === 0 ? (
                  <div className="text-success small text-center mt-2 fw-semibold">✓ Saved</div>
                ) : (
                  <div className="text-muted small text-center mt-2">
                    {pendingCount === 0
                      ? mode === 'update'
                        ? 'No pending changes'
                        : 'Defaults only'
                      : `${pendingCount} pending change${pendingCount !== 1 ? 's' : ''}`}
                  </div>
                )}
                {submitHint && <div className="text-muted small text-center mt-1">{submitHint}</div>}
              </Card.Body>
            </Card>
          </div>
        </Col>

        {/* Section cards */}
        <Col md={8} lg={9} xl={10}>
          {filtersActive && !CONFIG_SECTIONS.some((s) => sectionHasContent(s.id)) && (
            <Alert variant="info">No settings match the current search/filter.</Alert>
          )}
          {CONFIG_SECTIONS.map((s) => renderSection(s.id))}
        </Col>
      </Row>
    </Form>
  );
}

export default ClientConfigForm;
